import React, { ChangeEvent, useCallback } from "react";
import { Box, Button, Stack, TextField } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AuthenticationContext } from "../../components/authentication/AuthenticationContext";
import { FileResponse } from "../../types/FileResponse";
import { Album as AlbumType } from '../../types/Album';
import { getAlbum, getAlbumCoverWithoutCache, postAlbum, postAlbumImage } from "../../components/apis/music-api";
import { BeatLoader } from "react-spinners";
import { isNumeric, splitAlbumName } from "../../components/text-helper/text-helper";
import ButtonBar from "../../components/button-bar/ButtonBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCloudArrowUp, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { postAlbumRequest } from "../../types/messages/postAlbumRequest";
import callApi from "../../components/call-api/call-api";

type EditAlbumProps = {
    letter: string,
    artistName: string,
    albumName: string,
}

const EditAlbum = () => {
    const params = useParams<EditAlbumProps>();
    const navigate = useNavigate();
    const { getAccessToken } = useContext(AuthenticationContext);
    const [ album, setAlbum ] = useState<AlbumType>({ letter: '', artistName: '', year: 0, title: '', thumbnail: new Uint8Array(0), trackCount: 0, totalDuration: 0 });
    const [ cover, setCover ] = useState<FileResponse>();
    const [ selectedFile, setSelectedFile ] = useState<File | null>(null);
    const [ title, setTitle ] = useState('');
    const [ year, setYear ] = useState('1970');
    
    const handleAlbumCover = useCallback((artistName?: string, albumName?: string) => {
        callApi<FileResponse>(getAlbumCoverWithoutCache(getAccessToken(), artistName ?? '', albumName ?? ''), setCover);
    }, [getAccessToken]);
    
    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => { 
        if (event.target.files && event.target.files.length > 0) {
            setSelectedFile(event.target.files[0]);
        }
    }
    
    const fileToUint8Array = async (file: File): Promise<Uint8Array> => {
        const arrayBuffer = await file.arrayBuffer();
        return new Uint8Array(arrayBuffer);
    }
    
    const save = async () => {
        const a: postAlbumRequest = {
            letter: params.letter ?? '',
            artistName: params.artistName ?? '',
            year: parseInt(year),
            title: title,
        }

        await postAlbum(getAccessToken(), params.artistName ?? '', params.albumName ?? '', a);
        navigate(`/letters/${params.letter}/artists/${params.artistName}/albums/${year} - ${title}`);
    }

    const handleSave = () => {
        if (params.letter !== undefined && params.artistName !== undefined && params.albumName !== undefined) {
            save();
        }        
    }

    const handleUpload = () => {
        if (selectedFile) {
            fileToUint8Array(selectedFile).then((f) => {
                const formData = new FormData();
                const byteArray = new Uint8Array(f);
                const blob = new Blob([byteArray]);
                formData.append('file', blob, 'cover.jpg');
                if (params.letter !== undefined && params.artistName !== undefined && params.albumName !== undefined) {
                    postAlbumImage(getAccessToken(), params.artistName, params.albumName, 'cover.jpg', formData);
                }
                                
                setTimeout(() => handleAlbumCover(params.artistName, params.albumName), 2000);
            });

            setSelectedFile(null); // Reset the selected file after upload
        }
    };

    useEffect(() => {
        callApi<AlbumType>(getAlbum(getAccessToken(), params.artistName ?? '', params.albumName ?? ''), setAlbum);
    }, [params, getAccessToken]);

    useEffect(() => {
        if (album) {
            handleAlbumCover(params.artistName, params.albumName);
        }
    }, [album, handleAlbumCover, params.letter, params.artistName, params.albumName, getAccessToken]);

    useEffect(() => {
        if (params.albumName !== undefined) {
            const { year, title } = splitAlbumName(params.albumName ?? '');
            console.log('title:', title);
            setYear(year);
            setTitle(title);
        }
    }, [params.albumName]);

    return (
        <>
            <ButtonBar
                leftButtons={[
                    <Button component={Link} variant="contained" size="small" to={`/letters/${params.letter}/artists/${(params.artistName ?? '')}/albums/${(params.albumName ?? '')}`}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faArrowLeft} className="icon"/>
                        Back To Album
                    </Button>
                ]}
                rightButtons={[
                    <Button variant="contained" size="small" onClick={handleSave}>
                        <FontAwesomeIcon fontSize="small" color="primary" icon={faPenToSquare} className="icon"/>
                        Save Changes
                    </Button>
                ]}
            />
            <Stack direction="row" spacing={3}>
                <Box sx={{ width: '500px', height: '500px', textAlign: "center" }}>
                    <Box component="img" alt={'Album Cover'} sx={{ width: '500px', height: '500px', border: '1px solid #888' }} src={`data:image/jpeg;base64,${cover?.fileContents}`} />
                    <BeatLoader
                        loading={cover?.fileContents === undefined}
                        size={24}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                    />
                </Box>
                <Box sx={{ width: '100%' }}>
                    <Stack spacing={1} direction="row" sx={{ paddingBottom: '24px' }}>
                        <Box><TextField id="album-year" sx={{ width: '80px' }} size="small" label="Year" variant="outlined" value={year} onChange={(e) => isNumeric(e.target.value) && setYear(e.target.value)} /></Box>
                        <Box><TextField id="album-title" sx={{ width: '420px' }} size="small" label="Title" variant="outlined" value={title} onChange={(e) => setTitle(e.target.value)} /></Box>                    
                    </Stack>
                    <Stack direction="row">
                        <Box>
                            <TextField type="file" variant="outlined" size="small" margin="none" onChange={handleFileChange} />
                        </Box> 
                        <Box sx={{ paddingLeft: '18px' }}> 
                            <Button variant="contained" color="primary" onClick={handleUpload} disabled={!selectedFile}>
                                <FontAwesomeIcon fontSize="large" color="primary" icon={faCloudArrowUp} className="icon"/>
                                Upload Cover
                            </Button>
                        </Box> 
                    </Stack>
                </Box>
            </Stack>
        </>
    );
}

export default EditAlbum;
